import React, { useState, useRef, useEffect } from 'react';
import { Table, Input, Button, Space, Tag, Drawer, message } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import * as userAPI from '../../models/userAPI';
import CreateUserForm from './CreateUserForm';

const UsersList = ({ onSelectUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const searchInput = useRef(null);
  
  useEffect(() => {
    let active = true;
    const refresh = () => {
      setLoading(true);
      userAPI.getAllUsers()
      .then(response => {
        if (!active) return;
        setUsers(response.data);
        setError(null);
        setLoading(false);
      })
      .catch(err => {
        if (!active) return;
        setError(err.message);
        setLoading(false);
      });
    };
    refresh();
    window.addEventListener('permission-groups-changed', refresh);
    window.addEventListener('permission-memberships-changed', refresh);
    return () => {
      active = false;
      window.removeEventListener('permission-groups-changed', refresh);
      window.removeEventListener('permission-memberships-changed', refresh);
    };
  }, []);

  const handleSelect = (user) => {
    onSelectUser(user.UserID);  // Pass the selected user ID up to the parent component
  };

  const handleCreateSuccess = () => {
    // Close the drawer first
    setDrawerVisible(false);
    
    // Then manually refresh user data
    setLoading(true);
    userAPI.getAllUsers()
      .then(response => {
        setUsers(response.data);
        setLoading(false);
        message.success('Users list refreshed');
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
        message.error('Failed to refresh users list');
        console.error('Error refreshing users:', err);
      });
  };

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current && searchInput.current.select());
      }
    },
    render: text => searchedColumn === dataIndex ? (
      <span style={{ backgroundColor: '#ffc069', padding: 0 }}>
        {text ? text.toString().split(new RegExp(`(${searchText})`, 'gi')).map((fragment, i) => 
          fragment.toLowerCase() === searchText.toLowerCase() ? (
            <mark key={i} style={{ padding: 0, backgroundColor: '#ffc069' }}>{fragment}</mark>
          ) : (
            fragment
          )
        ) : text}
      </span>
    ) : text,
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = clearFilters => {
    clearFilters();
    setSearchText('');
  };

  if (error) return <p>Error: {error}</p>;

  const columns = [
    {
      title: 'Name',
      dataIndex: 'Name',
      key: 'Name',
      sorter: (a, b) => a.Name.localeCompare(b.Name),
      ...getColumnSearchProps('Name'),
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'Email',
      ...getColumnSearchProps('Email'),
    },
    {
      title: 'Role',
      dataIndex: 'IsManager',
      key: 'role',
      render: isManager => (
        <Tag color={isManager ? 'green' : 'blue'}>
          {isManager ? 'Manager' : 'Standard User'}
        </Tag>
      ),
      filters: [
        { text: 'Manager', value: true },
        { text: 'Standard User', value: false },
      ],
      onFilter: (value, record) => record.IsManager === value,
    },
    {
      title: 'Groupes de permissions',
      key: 'groups',
      render: (_, record) => (record.PermissionGroups || []).map(group => <Tag key={group.id} color="blue">{group.Name}</Tag>),
    },
    {
      title: 'Permissions',
      key: 'permissions',
      render: (_, record) => {
        const permissionCount = Object.values(record.EffectivePermissions || {}).reduce((total, actions) => total + Object.values(actions).filter(Boolean).length, 0);
        
        return (
          <Tag color={permissionCount > 5 ? 'green' : permissionCount > 2 ? 'blue' : 'orange'}>
            {permissionCount} permissions
          </Tag>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      render: date => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.CreatedAt) - new Date(b.CreatedAt),
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>Users List</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => setDrawerVisible(true)}
        >
          Create User
        </Button>
      </div>
      
      <Table
        columns={columns}
        dataSource={users}
        rowKey="UserID"
        loading={loading}
        pagination={{ pageSize: 10 }}
        onRow={(record) => ({
          onClick: () => handleSelect(record),
          style: { cursor: 'pointer' }
        })}
      />

      <Drawer
        title="Create New User"
        width={520}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
        destroyOnClose={true}
      >
        <CreateUserForm onSuccess={handleCreateSuccess} />
      </Drawer>
    </div>
  );
};

export default UsersList;
